import React from 'react';
import DetailPlayerCard from '@/app/components/detail_player_card';
import FillOutProfile from '@/app/features/home/components/fill_out_profile/widget';
import Input from '@/app/components/input';
import DatePicker from '@/app/components/date_picker';
import Checkbox from '@/app/components/checkbox';
import CityAutocomplete from '@/app/components/city_autocomplete/widget';
import Button from '@/app/components/button';
import {TextMedium14} from '@/app/components/texts';
import LazyAssets from '@/app/utils/assets_widget';
import {t} from 'i18next';
import {s} from '@/app/utils/s';
import classNames from 'classnames';
import {
  GenderEnum,
  IndustryEnum,
  MemberOfPadelClubEnum,
  NprpRatingEnum,
  PlayingFrequencyEnum,
  SkillLevelEnum
} from '@/app/api/user';
import styles from '../styles.module.scss';

// Import custom components
import ProfileField from './profile_field';
import ChipSelection from './chip_selection';
import ProfilePhoto from './profile_photo';
import LogoutButton from './logout_button';
import LogoutDialog from './logout_dialog';

// Import types
import {ProfileState, ProfileActions} from '../types';

interface ProfileDesktopViewProps {
  state: ProfileState;
  actions: ProfileActions;
}

/**
 * Desktop view for the Profile page
 */

const ProfileDesktopView: React.FC<ProfileDesktopViewProps> = ({state, actions}) => {
  const {
    user,
    isEdit,
    editedUser,
    photo,
    showWantToLeave
  } = state;

  const {
    goToOnboarding,
    setIsEdit,
    setName,
    onSelect,
    setPadelSkillLevel,
    setNtrpRating,
    setPadelClubMembership,
    setPlayingFrequency,
    setDateOfBirth,
    toggleShowInProfileDateOfBirth,
    setGender,
    setAboutMe,
    setIndustry,
    setInstagram,
    setLinkedin,
    uploadPhoto,
    onSave,
    onLogout,
    toggleShowWantToLeave
  } = actions;

  console.log(editedUser)

  return (
    <div className={styles.profile}>
      <div className={styles.sideBar}>
        <Button
          text={t(s.profile)}
          icon={<LazyAssets.Profile/>}
          onClick={() => {
          }}
          isGreen
          alignLeft
          smallRadius
        />
        <LogoutButton
          onClick={toggleShowWantToLeave}
          isDesktop
        />
      </div>

      <div className={classNames(styles.main, {[styles.edit]: isEdit})}>
        {!isEdit && (
          <>
            {user?.user?.imageLink === null && (
              <FillOutProfile
                goToOnboarding={goToOnboarding}
                user={user}
              />
            )}
            <div className={styles.card}>
              <div className={styles.topBar} onClick={() => setIsEdit(true)}>
                <div className={styles.hoverWrapper}>
                  <LazyAssets.Edit/>
                  <TextMedium14 text={t(s.edit)}/>
                </div>
              </div>
              <DetailPlayerCard userBig={user}/>
            </div>
          </>
        )}

        {isEdit && (
          <div className={styles.listWithFields}>
            <div className={styles.topBar} onClick={onSave}>
              <div className={styles.hoverWrapper}>
                <LazyAssets.Edit/>
                <TextMedium14 text={t(s.done)}/>
              </div>
            </div>

            <ProfilePhoto
              imageLink={editedUser?.user.imageLink}
              username={editedUser?.user.username ?? ""}
              photoBase64={photo}
              onUploadPhoto={uploadPhoto}
            />

            <Input
              label={t(s.addUsername)}
              onChange={setName}
              value={editedUser?.user.username}
              isWhite
            />

            <ProfileField title={s.yourCity}>
              <CityAutocomplete
                onCitySelect={({name}) => onSelect(name)}
                value={editedUser?.city}
                isWhite
              />
            </ProfileField>

            {editedUser?.ntrpRating ? <ProfileField title={s.padelSkillLevel}>
              <ChipSelection
                items={NprpRatingEnum.values}
                selectedItem={editedUser?.ntrpRating}
                onChange={setNtrpRating}
                isBig
              />
            </ProfileField> : <ProfileField title={s.padelSkillLevel}>
              <ChipSelection
                items={SkillLevelEnum.values}
                selectedItem={editedUser?.skillLevel}
                onChange={setPadelSkillLevel}
                isBig
              />
            </ProfileField>}
            <ProfileField title={s.participationInPadleSportsClub}>
              <ChipSelection
                items={MemberOfPadelClubEnum.values}
                selectedItem={editedUser?.padelClubMembership}
                onChange={setPadelClubMembership}
                isBig
              />
            </ProfileField>

            <ProfileField title={s.howOftenPlayPadel}>
              <ChipSelection
                items={PlayingFrequencyEnum.values}
                selectedItem={editedUser?.user.playFrequency}
                onChange={setPlayingFrequency}
                isBig
              />
            </ProfileField>

            <ProfileField title={s.dateOfBirthOnly}>
              <DatePicker
                initialDate={editedUser?.dateOfBirth ? new Date(editedUser.dateOfBirth) : new Date('2000-01-01')}
                onChange={setDateOfBirth}
                className={styles.datePicker}
                isWhite
                disablePast={false}
              />
              {/*<Checkbox<string>*/}
              {/*  items={[s.showInProfile]}*/}
              {/*  onChange={toggleShowInProfileDateOfBirth}*/}
              {/*  useS={true}*/}
              {/*  className={styles.checkbox}*/}
              {/*/>*/}
            </ProfileField>

            <ProfileField
              title={s.genderOnly}
              description={s.preferToPlayMixedDoubles}
            >
              <ChipSelection
                items={GenderEnum.values}
                selectedItem={editedUser?.gender}
                onChange={setGender}
                isBig
              />
              {/*<Checkbox<string>*/}
              {/*  items={[s.showInProfile]}*/}
              {/*  onChange={toggleShowInProfileDateOfBirth}*/}
              {/*  useS={true}*/}
              {/*  className={styles.checkbox}*/}
              {/*/>*/}
            </ProfileField>

            <ProfileField title={s.playerBio}>
              <Input
                onChange={setAboutMe}
                hint={t(s.infoAboutYourselfDescription)}
                className={styles.inputAboutMe}
                value={editedUser?.aboutYourself}
                limit={200}
                isTextArea
                isWhite
              />
            </ProfileField>

            <ProfileField
              title={s.addYourIndustry}
              description={s.peopleCommunicateWhenThereAreCommonTopics}
            >
              <ChipSelection
                items={IndustryEnum.values}
                selectedItems={(editedUser?.industry ? 
                  editedUser.industry.split(',')
                    .filter(item => item && Object.values(IndustryEnum).includes(item as IndustryEnum))
                    .map(item => item as IndustryEnum) 
                  : [])}
                onChange={setIndustry}
                isMultiSelect
                isIndustries
                isBig
              />
            </ProfileField>

            <ProfileField title={s.addSocialLinks}>
              <Input
                onChange={setInstagram}
                hint={"makiv_tsvit"}
                label={t(s.instagram)}
                value={editedUser?.instagram}
                className={styles.link}
                isWhite
              />
              <Input
                onChange={setLinkedin}
                hint={"https://www.linkedin.com"}
                label={t(s.linkedin)}
                value={editedUser?.linkedin}
                className={styles.link}
                isWhite
              />
            </ProfileField>
          </div>
        )}
      </div>

      <LogoutDialog
        isOpen={showWantToLeave ?? false}
        onClose={toggleShowWantToLeave}
        onLogout={onLogout}
      />
    </div>
  );
};

export default ProfileDesktopView;